import { useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useAuraPoints } from './useAuraPoints';
import { useStreaks } from './useStreaks';
import { useAchievements } from './useAchievements';
import { useStyleCredits } from './useStyleCredits';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const DAILY_LOGIN_KEY = 'aura_last_login_date';
const DAILY_CREDIT_BONUS = 5;

export const useDailyLogin = () => {
  const { user } = useAuth();
  const { addPoints } = useAuraPoints();
  const { checkAndUpdateStreak, getCurrentMultiplier, streak } = useStreaks();
  const { checkStreakAchievements, checkSecretAchievements } = useAchievements();
  const { refreshBalance } = useStyleCredits();

  const awardDailyCredits = useCallback(async () => {
    if (!user) return;

    try {
      // Add credits via edge function
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: {
          action: 'add_credits',
          amount: DAILY_CREDIT_BONUS,
          reason: 'daily_login_bonus',
        },
      });

      if (error) throw error;

      if (data?.success) {
        // Refresh balance to show updated credits
        await refreshBalance();
        toast.success(`+${DAILY_CREDIT_BONUS} Style Credits for daily login!`, {
          icon: '💎',
          duration: 3000,
        });
      }
    } catch (err) {
      console.error('Failed to award daily credits:', err);
    }
  }, [user, refreshBalance]);

  const checkAndAwardDailyBonus = useCallback(async () => {
    if (!user) return;

    const today = new Date().toISOString().split('T')[0];
    const lastLogin = localStorage.getItem(`${DAILY_LOGIN_KEY}_${user.id}`);

    if (lastLogin !== today) {
      // Update streak first
      const streakResult = await checkAndUpdateStreak();
      
      // Calculate multiplier for bonus points
      const multiplier = getCurrentMultiplier();
      
      // Award daily login bonus with streak multiplier (Aura Points)
      await addPoints('daily_login', true);
      
      // Award daily Style Credits bonus
      await awardDailyCredits();
      
      // Extra bonus for streak multiplier
      if (multiplier > 1 && streakResult?.streak && streakResult.streak > 1) {
        // Bonus points already factored into the streak system display
      }
      
      // Check for streak achievements
      if (streakResult?.streak) {
        await checkStreakAchievements(streakResult.streak);
      }
      
      // Check for secret time-based achievements
      await checkSecretAchievements();
      
      localStorage.setItem(`${DAILY_LOGIN_KEY}_${user.id}`, today);
    }
  }, [user, addPoints, checkAndUpdateStreak, getCurrentMultiplier, checkStreakAchievements, checkSecretAchievements, awardDailyCredits]);

  useEffect(() => {
    if (user) {
      // Small delay to ensure profile is loaded
      const timer = setTimeout(() => {
        checkAndAwardDailyBonus();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, checkAndAwardDailyBonus]);

  return { checkAndAwardDailyBonus, streak, multiplier: getCurrentMultiplier() };
};
